import { computed, ref, toValue, type Ref, type UnwrapNestedRefs } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import { useChannelCapabilities } from "@/composables/use-channel-capabilities";
import { graphql as gql } from "@/generated";
import { apolloClient } from "@/utils/apollo";
import { useToastStore } from "@/stores/toast.store";
import { useTrackingStore } from "@/stores/tracking.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useCapabilitiesStore } from "@/stores/capabilities.store";
import { useQuery } from "@urql/vue";

export enum PurchaseStatus {
  Pending = "pending",
  Active = "active",
  Cancelled = "cancelled",
  Expired = "expired",
  Refunded = "refunded",
}

export enum ProductType {
  Subscription = "subscription",
  OneTime = "one_time",
  Consumable = "consumable",
}

// GraphQL Operations for Product-based purchases

const GetSubscriptionProductsDocument = gql(/* GraphQL */ `
  query GetSubscriptionProducts(
    $benefit_types: [ProductBenefitType!]
    $exclude_benefit_types: [ProductBenefitType!]
  ) {
    getSubscriptionProducts(
      benefit_types: $benefit_types
      exclude_benefit_types: $exclude_benefit_types
    ) {
      ... on ProductList {
        products {
          id
          name
          description
          stripe_price_id
          product_type
          is_active
          action_label
          benefit_descriptions
          localised_price {
            amount
            currency
            display_label
          }
          benefits {
            id
            benefit_type
            channel_membership {
              id
              name
              permission_mask
              permission_mask_level
            }
            event_id
            credits_amount
            duration_days
            points_multiplier
          }
        }
      }
      ... on NoChannelError {
        error
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);

const InitiatePurchaseDocument = gql(/* GraphQL */ `
  mutation InitiatePurchase($product_id: String!) {
    initiatePurchase(product_id: $product_id) {
      ... on InitiatePurchaseResult {
        purchase_id
        subscription_id
        client_secret
        publishable_key
        price
      }
      ... on UnauthenticatedError {
        error
      }
      ... on NoChannelError {
        error
      }
      ... on ProductNotFoundError {
        error
        product_id
      }
      ... on AlreadyHasActiveSubscriptionError {
        error
        channel_id
      }
      ... on StripeConfigNotFoundError {
        error
        channel_id
      }
      ... on NoPaymentMethodError {
        error
      }
      ... on NoAutomaticChargeError {
        error
        client_secret
        payment_intent_id
        publishable_key
        payment_method_id
        amount_due
        currency
        proration_amount_display
      }
      ... on StripeError {
        error
        message
      }
    }
  }
`);

export const CheckPurchasePaymentStatusDocument = gql(/* GraphQL */ `
  query CheckPurchasePaymentStatus($payment_intent_id: String!) {
    checkPurchasePaymentStatus(payment_intent_id: $payment_intent_id) {
      ... on PaymentStatusResult {
        status
        purchase_activated
      }
      ... on UnauthenticatedError {
        error
      }
      ... on NoChannelError {
        error
      }
      ... on StripeError {
        error
        message
      }
    }
  }
`);

const CancelSubscriptionDocument = gql(/* GraphQL */ `
  mutation CancelSubscription {
    cancelSubscription {
      ... on CancelPurchaseResult {
        cancelled_at
      }
      ... on UnauthenticatedError {
        error
      }
      ... on NoChannelError {
        error
      }
      ... on SubscriptionNotFoundError {
        error
      }
      ... on StripeError {
        error
        message
      }
    }
  }
`);

const ReactivateSubscriptionDocument = gql(/* GraphQL */ `
  mutation ReactivateSubscription($subscription_id: String) {
    reactivateSubscription(subscription_id: $subscription_id) {
      ... on ReactivatePurchaseResult {
        status
      }
      ... on UnauthenticatedError {
        error
      }
      ... on NoChannelError {
        error
      }
      ... on SubscriptionNotFoundError {
        error
      }
      ... on StripeError {
        error
        message
      }
    }
  }
`);

const GetUserPurchasesDocument = gql(/* GraphQL */ `
  query GetUserPurchases {
    getUserPurchases {
      ... on UserPurchaseList {
        purchases {
          id
          status
          purchased_at
          expires_at
          cancelled_at
          is_renewal
          stripe_subscription_id
          stripe_invoice_id
          payment_method_last4
          next_billing_date
          product {
            id
            name
            description
            product_type
            benefit_descriptions
            localised_price {
              amount
              currency
              display_label
            }
            benefits {
              id
              benefit_type
              points_multiplier
            }
          }
        }
      }
      ... on UnauthenticatedError {
        error
      }
      ... on NoChannelError {
        error
      }
    }
  }
`);

const GetUserPurchaseHistoryDocument = gql(/* GraphQL */ `
  query GetUserPurchaseHistory {
    getUserPurchaseHistory {
      ... on UserPurchaseList {
        purchases {
          id
          status
          purchased_at
          expires_at
          cancelled_at
          is_renewal
          stripe_subscription_id
          stripe_invoice_id
          payment_method_last4
          next_billing_date
          product {
            id
            name
            description
            product_type
            benefit_descriptions
            localised_price {
              amount
              currency
              display_label
            }
            benefits {
              id
              benefit_type
              points_multiplier
            }
          }
        }
      }
      ... on UnauthenticatedError {
        error
      }
      ... on NoChannelError {
        error
      }
    }
  }
`);

const GetInvoiceUrlsDocument = gql(/* GraphQL */ `
  query GetInvoiceUrls($purchase_id: String!) {
    getInvoiceUrls(purchase_id: $purchase_id) {
      ... on InvoiceUrlsResult {
        hosted_invoice_url
        invoice_pdf
      }
      ... on UnauthenticatedError {
        error
      }
      ... on NoChannelError {
        error
      }
      ... on PurchaseNotFoundError {
        error
        purchase_id
      }
      ... on SubscriptionNotFoundError {
        error
      }
      ... on StripeError {
        error
        message
      }
    }
  }
`);

/**
 * Composable to fetch subscription products with useQuery.
 *
 * @param options.enabled - Ref or computed that controls when the query is executed
 * @param options.benefitTypes - Only include products with these benefit types
 * @param options.excludeBenefitTypes - Exclude products with these benefit types
 * @returns products, loading state, error, and refetch function
 */
export function useSubscriptionProducts(options?: {
  enabled?: Ref<boolean> | (() => boolean);
  benefitTypes?: string[];
  excludeBenefitTypes?: string[];
}) {
  const {
    data: result,
    fetching: loading,
    error,
    executeQuery: refetch,
  } = useQuery({
    query: GetSubscriptionProductsDocument,
    variables: {
      benefit_types: options?.benefitTypes ?? null,
      exclude_benefit_types: options?.excludeBenefitTypes ?? null,
    },
    pause: () => !(toValue(options?.enabled) ?? true),
  });

  const products = computed<ProductInfo[] | null>(() => {
    const response = result.value?.getSubscriptionProducts;
    if (!response || "error" in response) return [];
    if ("products" in response) {
      return response.products as ProductInfo[];
    }

    return null;
  });

  const subscriptionProducts = computed(
    () =>
      products.value?.filter(
        (p) => p.product_type === ProductType.Subscription && p.is_active,
      ) ?? null,
  );

  return {
    products,
    subscriptionProducts,
    loading,
    error,
    refetch,
  };
}

export interface ProductInfo {
  id: string;
  name: string;
  description?: string | null;
  stripe_price_id?: string | null;
  product_type: ProductType;
  is_active: boolean;
  action_label?: string | null;
  benefit_descriptions: string[];
  localised_price?: {
    amount: number;
    currency: string;
    display_label: string;
  } | null;
  benefits: Array<{
    id: string;
    benefit_type: string;
    channel_membership?: {
      id: string;
      name: string;
      permission_mask: number;
      permission_mask_level: number;
    } | null;
    points_multiplier?: number | null;
    event_id?: string | null;
    credits_amount?: number | null;
    duration_days?: number | null;
  }>;
}

export interface PurchaseInfo {
  id: string;
  status: PurchaseStatus;
  purchased_at: Date;
  expires_at?: Date | null;
  cancelled_at?: Date | null;
  is_renewal: boolean;
  stripe_subscription_id?: string | null;
  stripe_invoice_id?: string | null;
  payment_method_last4?: string | null;
  next_billing_date?: Date | null;
  product: {
    id: string;
    name: string;
    description?: string | null;
    product_type: ProductType;
    benefit_descriptions?: string[];
    localised_price?: {
      amount: number;
      currency: string;
      display_label: string;
    } | null;
    benefits?: Array<{
      id: string;
      benefit_type: string;
      points_multiplier?: number | null;
    }>;
  };
}

export interface InitiatePurchaseResult {
  purchaseId: string;
  subscriptionId?: string;
  clientSecret?: string;
  publishableKey?: string;
  price?: string;
}

/**
 * Service for product-based purchase operations.
 *
 * This is the new purchase flow that supports:
 * - Subscription products (memberships)
 * - One-time products
 * - Consumable products
 *
 * @internal Use `usePurchaseService()` to access this service.
 */
export function setupPurchaseService() {
  const channelStore = useChannelStore();
  const { isMembershipsEnabled: _isMembershipsEnabled } =
    useChannelCapabilities();

  // State
  const isPurchasing = ref(false);
  const purchaseError = ref<string | null>(null);
  const products = ref<ProductInfo[]>([]);
  const userPurchases = ref<PurchaseInfo[]>([]);

  // Computed
  const subscriptionProducts = computed(() =>
    products.value.filter((p) => p.product_type === ProductType.Subscription),
  );

  const oneTimeProducts = computed(() =>
    products.value.filter((p) => p.product_type === ProductType.OneTime),
  );

  const activeSubscription = computed(() =>
    userPurchases.value.find(
      (p) =>
        p.product.product_type === ProductType.Subscription &&
        p.status === PurchaseStatus.Active,
    ),
  );

  /**
   * Fetch user's active/pending purchases for the channel.
   */
  async function fetchUserPurchases(): Promise<PurchaseInfo[]> {
    const result = await apolloClient.query({
      query: GetUserPurchasesDocument,
      fetchPolicy: "network-only",
    });

    const response = result.data?.getUserPurchases;

    if (!response || "error" in response) {
      console.error(
        "[PurchaseService] Error fetching user purchases",
        response,
      );
      return [];
    }

    if ("purchases" in response) {
      userPurchases.value = response.purchases.map((p) => ({
        ...p,
        purchased_at: new Date(p.purchased_at),
        expires_at: p.expires_at ? new Date(p.expires_at) : null,
        cancelled_at: p.cancelled_at ? new Date(p.cancelled_at) : null,
      })) as PurchaseInfo[];
      return userPurchases.value;
    }

    return [];
  }

  /**
   * Fetch full purchase history for the user in the channel.
   * Includes all purchases (active, cancelled, expired, renewals, etc.)
   */
  async function fetchPurchaseHistory(): Promise<PurchaseInfo[]> {
    const result = await apolloClient.query({
      query: GetUserPurchaseHistoryDocument,
      fetchPolicy: "network-only",
    });

    const response = result.data?.getUserPurchaseHistory;

    if (!response || "error" in response) {
      console.error(
        "[PurchaseService] Error fetching purchase history",
        response,
      );
      return [];
    }

    if ("purchases" in response) {
      return response.purchases.map((p) => ({
        ...p,
        purchased_at: new Date(p.purchased_at),
        expires_at: p.expires_at ? new Date(p.expires_at) : null,
        cancelled_at: p.cancelled_at ? new Date(p.cancelled_at) : null,
      })) as PurchaseInfo[];
    }

    return [];
  }

  /**
   * Initiate a purchase for a product.
   */
  async function initiatePurchase(
    productId: string,
  ): Promise<InitiatePurchaseResult | { error: string; message?: string }> {
    isPurchasing.value = true;
    purchaseError.value = null;

    try {
      const result = await apolloClient.mutate({
        mutation: InitiatePurchaseDocument,
        variables: { product_id: productId },
      });

      const response = result.data?.initiatePurchase;

      if (!response) {
        return { error: "UnknownError", message: "No response from server" };
      }

      if ("error" in response) {
        purchaseError.value = response.error;
        return {
          error: response.error,
          message: "message" in response ? response.message : undefined,
        };
      }

      return {
        purchaseId: response.purchase_id,
        subscriptionId: response.subscription_id ?? undefined,
        clientSecret: response.client_secret ?? undefined,
        publishableKey: response.publishable_key ?? undefined,
        price: response.price ?? undefined,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      purchaseError.value = message;
      return { error: "NetworkError", message };
    } finally {
      isPurchasing.value = false;
    }
  }

  /**
   * Poll for purchase payment completion after Stripe redirect.
   */
  async function pollPurchasePaymentStatus(
    paymentIntentId: string,
    retryCount = 0,
    maxRetries = 15,
  ): Promise<{ status: string; activated: boolean } | undefined> {
    if (retryCount > maxRetries) {
      throw new Error("Retry limit reached");
    }

    console.log(
      "[PurchaseService] Polling purchase payment status...",
      paymentIntentId,
    );

    const result = await apolloClient.query({
      query: CheckPurchasePaymentStatusDocument,
      variables: { payment_intent_id: paymentIntentId },
      fetchPolicy: "network-only",
    });

    const response = result.data?.checkPurchasePaymentStatus;

    if (result.errors || !response) {
      console.error(
        "[PurchaseService] Error checking payment status",
        result.errors,
      );
      throw new Error("Error checking payment status");
    }

    const toastStore = useToastStore();
    const trackingService = useTrackingStore();

    if ("error" in response) {
      if (response.error === "StripeError" && "message" in response) {
        // Payment might still be processing, retry
        console.log(
          `[PurchaseService] Payment processing, retrying...`,
          response,
        );
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return pollPurchasePaymentStatus(
          paymentIntentId,
          retryCount + 1,
          maxRetries,
        );
      }

      const message = "Error checking payment status: " + response.error;
      console.error(`[PurchaseService] Error:`, message);
      toastStore.addToast({ message, type: "error" });
      throw new Error(message);
    }

    if (response.status !== "succeeded" || !response.purchase_activated) {
      // Payment not yet complete, retry
      console.log(
        `[PurchaseService] Payment not complete, retrying...`,
        response,
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return pollPurchasePaymentStatus(
        paymentIntentId,
        retryCount + 1,
        maxRetries,
      );
    }

    // Payment successful and purchase activated
    await new Promise((resolve) => setTimeout(resolve, 2000));

    trackingService.track({
      event: TrackEventEnum.SubscriptionCompleted,
      category: EventCategory.Identity,
      metadata: {
        ...channelStore.gatingExperience,
      },
    });

    return {
      status: response.status,
      activated: response.purchase_activated,
    };
  }

  /**
   * Fetch invoice URLs (hosted invoice + PDF) for a purchase.
   */
  async function getInvoiceUrls(
    purchaseId: string,
  ): Promise<
    | { hostedInvoiceUrl: string | null; invoicePdf: string | null }
    | { error: string }
  > {
    const result = await apolloClient.query({
      query: GetInvoiceUrlsDocument,
      variables: { purchase_id: purchaseId },
      fetchPolicy: "network-only",
    });

    const response = result.data?.getInvoiceUrls;

    if (!response || "error" in response) {
      return { error: response?.error ?? "UnknownError" };
    }

    return {
      hostedInvoiceUrl: response.hosted_invoice_url ?? null,
      invoicePdf: response.invoice_pdf ?? null,
    };
  }

  /**
   * Cancel the active subscription.
   */
  async function cancelSubscription(): Promise<
    { cancelledAt: Date | null } | { error: string; message?: string }
  > {
    const result = await apolloClient.mutate({
      mutation: CancelSubscriptionDocument,
    });

    const response = result.data?.cancelSubscription;

    if (!response) {
      return { error: "UnknownError", message: "No response from server" };
    }

    if ("error" in response) {
      return {
        error: response.error,
        message: "message" in response ? response.message : undefined,
      };
    }

    return {
      cancelledAt: response.cancelled_at
        ? new Date(response.cancelled_at)
        : null,
    };
  }

  /**
   * Reactivate a cancelled subscription.
   * @param subscriptionId - Optional Stripe subscription ID to reactivate
   */
  async function reactivateSubscription(
    subscriptionId?: string,
  ): Promise<{ status: string } | { error: string; message?: string }> {
    const result = await apolloClient.mutate({
      mutation: ReactivateSubscriptionDocument,
      variables: { subscription_id: subscriptionId },
    });

    const response = result.data?.reactivateSubscription;

    if (!response) {
      return { error: "UnknownError", message: "No response from server" };
    }

    if ("error" in response) {
      return {
        error: response.error,
        message: "message" in response ? response.message : undefined,
      };
    }

    return { status: response.status };
  }

  /**
   * Refresh membership data after purchase.
   */
  async function refreshAfterPurchase(): Promise<void> {
    await channelStore.refreshPermissions();
    await channelStore.refreshUserInChannel();
    await fetchUserPurchases();
  }

  // Dispose function for cleanup
  function dispose() {
    isPurchasing.value = false;
    purchaseError.value = null;
    products.value = [];
    userPurchases.value = [];
  }

  const isReady = computed(() => true);

  return {
    // State
    isPurchasing,
    purchaseError,
    products,
    userPurchases,

    // Computed
    subscriptionProducts,
    oneTimeProducts,
    activeSubscription,

    // Actions
    fetchUserPurchases,
    fetchPurchaseHistory,
    initiatePurchase,
    getInvoiceUrls,
    pollPurchasePaymentStatus,
    cancelSubscription,
    reactivateSubscription,
    refreshAfterPurchase,

    // Lifecycle
    isReady,
    dispose,
  };
}

export type PurchaseService = UnwrapNestedRefs<
  ReturnType<typeof setupPurchaseService>
>;

/**
 * Access the purchase service. Throws if the service is not initialized.
 */
export function usePurchaseService(): PurchaseService {
  const store = useCapabilitiesStore();
  if (!store.purchaseService) {
    throw new Error("PurchaseService not initialized");
  }
  return store.purchaseService;
}

/**
 * Access the purchase service if available, or null if not initialized.
 */
export function useMaybePurchaseService(): PurchaseService | null {
  const store = useCapabilitiesStore();
  return store.purchaseService ?? null;
}
